import appConfig from '@/config/appConfig.typed';
import {DEVICE_TYPES} from '@/consts/device/deviceTypes';
import {getMappedInitialLanguageCode} from '@/services/localization/localizationService';
import {selectDeviceType} from '@/state/selectors/device';
import {getState} from '@/state/store';
import stringUtils from '@/utils/stringUtils';

type SsoTemplateFormatParams = Record<string, unknown>;

type GetFormattedSsoTemplateArgs = {
    ssoTemplate?: string | null;
    params?: SsoTemplateFormatParams;
};

/**
 * Maps device types that share the same ICC SSO configuration.
 * P1V41B uses the same SSO templates as P1V41M.
 */
const SSO_DEVICE_TYPE_ALIASES: Record<string, string> = {
    [DEVICE_TYPES.DEV_TYPE_P1V41B_CHARGER]: DEVICE_TYPES.DEV_TYPE_P1V41M_CHARGER,
    [DEVICE_TYPES.DEV_TYPE_P1V41B_HOLDER_CONTROL]: DEVICE_TYPES.DEV_TYPE_P1V41M_HOLDER_CONTROL,
};

const resolveDeviceTypeForSso = (deviceType: string | undefined): string | undefined => {
    if (!deviceType) return deviceType;
    return SSO_DEVICE_TYPE_ALIASES[deviceType] ?? deviceType;
};

/**
 * Interpolates market, language, device type, and custom params into an ICC SSO template name.
 */
export const getFormattedSsoTemplate = ({ssoTemplate, params}: GetFormattedSsoTemplateArgs): string | null => {
    if (!ssoTemplate) return null;

    const marketCode = appConfig.getIccMarketName?.()?.toUpperCase();
    const languageCode = getMappedInitialLanguageCode()?.toUpperCase();
    const deviceTypeFromState = selectDeviceType(getState?.());
    const deviceTypeFromParams = params?.deviceType;
    const deviceType = resolveDeviceTypeForSso(deviceTypeFromParams || deviceTypeFromState)?.toUpperCase();

    if (!languageCode || !marketCode) return null;

    return stringUtils.formatByName(ssoTemplate, {
        ...params,
        marketCode,
        languageCode,
        deviceType,
    });
};
